import { useEffect, useMemo, useState } from 'react'

import {
  ActionIcon,
  HoverCard,
  Popover,
  ScrollArea,
  Tooltip,
} from '@mantine/core'
import { ArrowUpRight, FileText } from 'lucide-react'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  GET_LICENSE_INTELLIGENCE,
  GET_MVR_FINDINGS,
  GET_PSP_FINDINGS,
} from 'src/graphql/documents.graphql'
import { DocumentCategory } from 'src/graphql/types/documents'
import { StepType } from 'src/graphql/types/steps'
import IconFlag from 'src/icons/IconFlag'
import {
  formatExtractedDetailValue,
  MVR_COUNTED_FINDING_TYPES,
  mvrFindingsFromExtractedRows,
  MvrExtractedFinding,
  PSP_COUNTED_FINDING_TYPES,
  pspFindingsFromExtractedRows,
} from 'src/lib/documentIntelligence'
import { formatStepType } from 'src/lib/formatters'

import {
  bestPredictionsByField,
  cdlHasValue,
  ComparisonFlag,
  evaluateRules,
  ExistenceFlag,
  Flag,
  FLAG_RULES,
  FlagGroupKey,
  LicenseValues,
  medicalHasValue,
  MvrScalarRow,
} from './aiFlagsRules'

// "Pending review" palette (doubleNickel-warning token) shared with
// AiMismatchChip in LicenseInformationCard and StepFindingsBadge so every AI
// finding surface reads as the same severity as a pending-review status.
// Expiration mismatches are always caution: both the document and the
// application carry a value, they just disagree — there is no "undisclosed"
// (omission) case for a credential expiration the way there is for PSP crashes.
const CHIP_BG = '#FFFAEB' // warning-50
const CHIP_BORDER = '#FEDF89' // warning-200
const CHIP_TEXT = '#B54708' // warning-700
const CAUTION_DOT = '#F79009' // warning-500
// MVR rows escalate to red when the AI-read accident count disagrees with what
// the driver disclosed — same severity rule MvrFindingsPill uses, so both MVR
// surfaces show the same color.
const ERROR_DOT = '#F04438' // error-500

// DOM ids placed on the application cards so each row's "go to" arrow can scroll
// to the right card. Exported so the cards and the pill stay in sync: CDL and
// DOT Med rows scroll to the License card, MVR rows to the Accidents card.
export const LICENSE_CARD_ANCHOR_ID = 'license-information-card'
export const ACCIDENTS_CARD_ANCHOR_ID = 'accidents-violations-card'

interface AiFlagsPillProps {
  applicantId: string
  license?: LicenseValues | null
  // Count of driver-disclosed accidents on the application. The MVR row turns
  // red when the AI-read accident count disagrees with this.
  disclosedAccidentCount?: number
}

// MVR finding types this application-header summary surfaces — the full set
// from documentIntelligence's MVR_COUNT_ROWS. Used for selecting the source MVR
// for EXISTENCE rules. Comparison rules with mvr-scalar sources (medical cert)
// use a separate "latest completed MVR" selection so they fire even when the
// newest MVR has no records-based findings.
const SURFACED_MVR_FINDING_TYPES = MVR_COUNTED_FINDING_TYPES

// PSP finding types this summary surfaces (ENG-4335). Same shape as
// SURFACED_MVR_FINDING_TYPES but for the PSP document — INSPECTION is included
// because it's what "any surfaced finding" means for PSP document selection.
// The existence rules in aiFlagsRules.ts further narrow which findings
// contribute to the incident-count red-dot (CRASH + VIOLATION only).
const SURFACED_PSP_FINDING_TYPES = PSP_COUNTED_FINDING_TYPES

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

// "1 accident · 3 violations · 1 disqualification · 1 suspension · 1 license
// action" — zero categories omitted. Aggregates existence flags by noun in
// most-serious-first order. Mirrors MvrFindingsPill so the AI Flags row and the
// standalone MVR pill read the same count. Extended in ENG-4335 to also cover
// PSP nouns for the PSP group's subtitle.
const buildFindingsCountLabel = (existenceFlags: ExistenceFlag[]): string => {
  // Order is important — disqualification before suspension before license
  // action. Use the noun's singular as the stable key for grouping.
  const ORDER = [
    'accident',
    'violation',
    'disqualification',
    'suspension',
    'license action',
    // PSP nouns match the singular values declared on the psp-crashes /
    // psp-violations existence rules (`{ singular: 'PSP crash', ... }`).
    'PSP crash',
    'PSP violation',
  ]
  const counts = new Map<string, { plural: string; n: number }>()
  for (const flag of existenceFlags) {
    const cur = counts.get(flag.noun.singular) ?? {
      plural: flag.noun.plural,
      n: 0,
    }
    cur.n++
    counts.set(flag.noun.singular, cur)
  }
  return ORDER.filter((k) => counts.has(k))
    .map((k) => {
      const { plural, n } = counts.get(k)!
      // Use the rule's declared plural directly (don't go through pluralize,
      // which appends 's' — would produce "violationss" for irregular nouns
      // whose plural isn't a clean +s).
      return `${n} ${n === 1 ? k : plural}`
    })
    .join(' · ')
}

// What each row in the popover dropdown needs to render its hover tooltip.
// Comparison rows show multi-source mismatches; existence rows show per-finding
// detail. Discriminated union so the renderer dispatches cleanly.
type RowData =
  | {
      kind: 'comparison'
      key: string
      label: string
      applicationValue: string
      // One entry per source that disagreed with the application. Single entry
      // is the common case ("CDL says X"); multi-entry appears when, e.g., both
      // CDL and MVR contradict the application's medical expiration.
      mismatches: ComparisonFlag['mismatches']
    }
  | {
      kind: 'existence'
      key: string
      label: string
      detail: string
      description?: string | null
      details?: { label: string; value: string }[]
    }

interface FlagGroup {
  key: FlagGroupKey
  label: string
  subtitle: string
  rows: RowData[]
  documentId: string | null
  // The application section to scroll to when the recruiter clicks the row's
  // "Go to application section" arrow. Null when no application section
  // corresponds to the flagged findings — e.g. an MVR group that only carries
  // disqualifications / suspensions / license actions, none of which are
  // disclosed on the Accidents card. The arrow is hidden in that case (the
  // "Open document" affordance remains available).
  anchorId: string | null
  dotColor: string
}

/**
 * AiFlagsPill — header pill that surfaces AI-extracted license mismatches
 * against the application: CDL license #, state, class, endorsements and
 * expiration, plus the Medical Certificate expiration (cross-validated against
 * both the DOT Medical card AND the MVR's MEDICAL_CERT_EXPIRATION). Plus an
 * MVR group with accidents/violations/suspensions/disqualifications/license
 * actions on the latest MVR. Renders nothing when document intelligence is
 * disabled or there are no flags.
 *
 * Compares against the *saved* application license (the `license` prop sourced
 * from GET_APPLICATION_BY_APPLICANT_ID). UPDATE_APPLICATION lists that query in
 * its `refetchQueries`, so the pill refreshes whenever the License card saves.
 * It intentionally does not read the card's unsaved draft state — the pill is a
 * summary of the persisted record, while the card shows live per-field chips
 * during an in-progress edit. The two reconcile on save.
 *
 * Rule-driven: comparison and existence rules are declared as data in
 * `aiFlagsRules.ts` and evaluated by `evaluateRules`. Adding a new comparison
 * source or a new existence-flag finding type is a one-entry change in that
 * file — this component handles ALL rules uniformly through the engine.
 */
const AiFlagsPill = ({
  applicantId,
  license,
  disclosedAccidentCount = 0,
}: AiFlagsPillProps) => {
  const currentCompany = useCurrentCompanyContext()
  const enabled =
    (currentCompany?.documentIntelligenceEnabled ?? false) && !!applicantId

  // The pill opens its dropdown on click and stays open until the recruiter
  // clicks the pill again, clicks outside, or presses Escape — so they can move
  // the cursor into the dropdown to open a document or jump to a section
  // without it closing. Click-outside and Escape are handled by Popover.
  const [opened, setOpened] = useState(false)

  const { data } = useQuery(GET_LICENSE_INTELLIGENCE, {
    variables: { applicantId: applicantId ?? '' },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  })

  const { data: mvrData } = useQuery(GET_MVR_FINDINGS, {
    variables: { applicantId: applicantId ?? '' },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  })

  const { data: pspData } = useQuery(GET_PSP_FINDINGS, {
    variables: { applicantId: applicantId ?? '' },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  })

  // ── MVR document selections ───────────────────────────────────────────────
  // Two selections from the MVR document list because comparison rules
  // (medical cert via MVR scalar) and existence rules (accidents/violations)
  // have different "what's the right source MVR" semantics:
  //   • Existence rules need the latest MVR with at least one surfaced finding
  //     — same selection MvrFindingsPill uses so the two surfaces describe the
  //     same document for findings.
  //   • Comparison rules with mvr-scalar sources need the latest COMPLETED MVR
  //     regardless of finding presence — a clean MVR with only a medical cert
  //     row should still produce a medical-expiration mismatch flag.

  const selectedMvrForExistence = useMemo<{
    findings: MvrExtractedFinding[]
    documentId: string | null
  }>(() => {
    if (!enabled) return { findings: [], documentId: null }
    for (const doc of mvrData?.documents?.items ?? []) {
      if (doc.documentIntelligence?.status !== 'COMPLETED') continue
      const f = mvrFindingsFromExtractedRows(
        doc.documentIntelligence.aiExtractedRows ?? []
      ).filter((finding) =>
        SURFACED_MVR_FINDING_TYPES.includes(finding.findingType)
      )
      if (f.length > 0) return { findings: f, documentId: doc.documentId }
    }
    return { findings: [], documentId: null }
  }, [mvrData, enabled])

  // ENG-4335: PSP mirror of selectedMvrForExistence. Newest PSP (by document
  // list order) that has at least one surfaced PSP finding.
  const selectedPspForExistence = useMemo<{
    findings: MvrExtractedFinding[]
    documentId: string | null
  }>(() => {
    if (!enabled) return { findings: [], documentId: null }
    for (const doc of pspData?.documents?.items ?? []) {
      if (doc.documentIntelligence?.status !== 'COMPLETED') continue
      const f = pspFindingsFromExtractedRows(
        doc.documentIntelligence.aiExtractedRows ?? []
      ).filter((finding) =>
        SURFACED_PSP_FINDING_TYPES.includes(finding.findingType)
      )
      if (f.length > 0) return { findings: f, documentId: doc.documentId }
    }
    return { findings: [], documentId: null }
  }, [pspData, enabled])

  const latestMvrForScalars = useMemo<{
    rows: MvrScalarRow[]
    documentId: string | null
  }>(() => {
    if (!enabled) return { rows: [], documentId: null }
    for (const doc of mvrData?.documents?.items ?? []) {
      if (doc.documentIntelligence?.status !== 'COMPLETED') continue
      // The aiExtractedRows read-model already exposes scalar rows (med cert
      // category/expiration) with their `value` populated and an empty `records`
      // list. We use them as-is.
      const rows: MvrScalarRow[] = (
        doc.documentIntelligence.aiExtractedRows ?? []
      )
        .filter((row) => !row.records || row.records.length === 0)
        .map((row) => ({ field: row.field, value: row.value ?? null }))
      return { rows, documentId: doc.documentId }
    }
    return { rows: [], documentId: null }
  }, [mvrData, enabled])

  // ── Evaluate rules ────────────────────────────────────────────────────────
  const flags = useMemo<Flag[]>(() => {
    const items = data?.documents?.items
    if (!enabled || !items) return []

    const completedOfCategory = (category: DocumentCategory) =>
      items.filter(
        (doc) =>
          doc.category === category &&
          doc.documentIntelligence?.status === 'COMPLETED'
      )

    return evaluateRules(FLAG_RULES, {
      license,
      cdlPredictions: bestPredictionsByField(
        completedOfCategory(DocumentCategory.CDL),
        cdlHasValue
      ),
      medicalPredictions: bestPredictionsByField(
        completedOfCategory(DocumentCategory.DOT_MEDICAL),
        medicalHasValue
      ),
      mvrScalarRows: latestMvrForScalars.rows,
      mvrScalarDocumentId: latestMvrForScalars.documentId,
      mvrFindings: selectedMvrForExistence.findings,
      mvrFindingsDocumentId: selectedMvrForExistence.documentId,
      // Non-admin users get empty PSP findings, so the psp-finding existence
      // rules match nothing and no PSP flags/group are emitted. The rules
      // themselves stay in FLAG_RULES — filtering by user role happens via
      // the empty pspFindings input rather than by conditionally building the
      // rule list, which keeps the rule set static and easier to reason about.
      pspFindings: selectedPspForExistence.findings,
      pspFindingsDocumentId: selectedPspForExistence.documentId,
    })
  }, [
    data,
    enabled,
    license,
    selectedMvrForExistence,
    selectedPspForExistence,
    latestMvrForScalars,
  ])

  // ── Build display groups from the flags ───────────────────────────────────
  const groups = useMemo<FlagGroup[]>(() => {
    if (flags.length === 0) return []

    const cdlFlags = flags.filter((f) => f.group === 'cdl')
    const medicalFlags = flags.filter((f) => f.group === 'medical')
    const mvrFlags = flags.filter((f) => f.group === 'mvr')
    const pspFlags = flags.filter((f) => f.group === 'psp')

    const result: FlagGroup[] = []

    // Comparison-only groups (CDL, medical) — subtitle is "N mismatches" and
    // each comparison flag becomes one popover row with a multi-source tooltip.
    const credentialGroups: Array<{ key: 'cdl' | 'medical'; label: string }> = [
      { key: 'cdl', label: formatStepType(StepType.CDL) },
      { key: 'medical', label: formatStepType(StepType.DOT_MEDICAL) },
    ]
    for (const { key, label } of credentialGroups) {
      const groupFlags = (key === 'cdl' ? cdlFlags : medicalFlags).filter(
        (f): f is ComparisonFlag => f.kind === 'comparison'
      )
      if (groupFlags.length === 0) continue
      result.push({
        key,
        label,
        subtitle: pluralize(groupFlags.length, 'mismatch'),
        rows: groupFlags.map((f) => ({
          kind: 'comparison',
          key: f.key,
          label: f.label,
          applicationValue: f.applicationValue,
          mismatches: f.mismatches,
        })),
        // One open-document action per row: in practice every winning
        // prediction for a category comes from the same upload, so a single
        // icon is correct.
        documentId:
          groupFlags.flatMap((f) => f.mismatches).find((m) => m.documentId)
            ?.documentId ?? null,
        anchorId: LICENSE_CARD_ANCHOR_ID,
        dotColor: CAUTION_DOT,
      })
    }

    // MVR group — built from existence flags. Subtitle aggregates by noun.
    if (mvrFlags.length > 0) {
      const existence = mvrFlags.filter(
        (f): f is ExistenceFlag => f.kind === 'existence'
      )
      // Red only when the MVR reveals MORE incidents than the driver disclosed
      // (hidden incidents) — same severity rule as MvrFindingsPill.
      const incidentCount = existence.filter(
        (f) => f.contributesToIncidentCount
      ).length
      const hasUndisclosedIncidents = incidentCount > disclosedAccidentCount
      const mvrDocumentId = existence.find((f) => f.documentId)?.documentId ?? null

      // Only route to the Accidents card when there's actually something on the
      // application that corresponds to the MVR findings. Accidents and
      // violations ARE disclosed there; disqualifications / suspensions /
      // license actions are NOT. When the MVR turned up only the latter, the
      // arrow used to scroll to an empty A&V card — confusing UX. Now we hide
      // the arrow entirely in that case; "Open document" still gets the user
      // to the MVR so they can read the disqualification detail. (ENG-4272)
      const anchorId = incidentCount > 0 ? ACCIDENTS_CARD_ANCHOR_ID : null

      result.push({
        key: 'mvr',
        label: 'MVR',
        subtitle: buildFindingsCountLabel(existence),
        rows: existence.map((f) => ({
          kind: 'existence',
          key: f.key,
          label: f.tooltipRow.label,
          detail: f.tooltipRow.detail ?? '',
          description: f.tooltipRow.description,
          details: f.tooltipRow.details,
        })),
        documentId: mvrDocumentId,
        anchorId,
        dotColor: hasUndisclosedIncidents ? ERROR_DOT : CAUTION_DOT,
      })
    }

    // PSP group (ENG-4335) — mirror of the MVR block above. Only non-empty
    // for admin users (non-admin: pspFindings = [] → no psp-finding rules
    // matched → pspFlags = []). Same severity rule as MVR: red when the PSP
    // reveals MORE incidents than the driver disclosed on the application.
    // Anchors to the Accidents & Violations card because that's where the
    // PSP crashes/violations get reconciled, matching the MVR anchor.
    if (pspFlags.length > 0) {
      const existence = pspFlags.filter(
        (f): f is ExistenceFlag => f.kind === 'existence'
      )
      const incidentCount = existence.filter(
        (f) => f.contributesToIncidentCount
      ).length
      const hasUndisclosedIncidents = incidentCount > disclosedAccidentCount
      const pspDocumentId =
        existence.find((f) => f.documentId)?.documentId ?? null
      const anchorId = incidentCount > 0 ? ACCIDENTS_CARD_ANCHOR_ID : null

      result.push({
        key: 'psp',
        label: 'PSP',
        subtitle: buildFindingsCountLabel(existence),
        rows: existence.map((f) => ({
          kind: 'existence',
          key: f.key,
          label: f.tooltipRow.label,
          detail: f.tooltipRow.detail ?? '',
          description: f.tooltipRow.description,
          details: f.tooltipRow.details,
        })),
        documentId: pspDocumentId,
        anchorId,
        dotColor: hasUndisclosedIncidents ? ERROR_DOT : CAUTION_DOT,
      })
    }

    return result
  }, [flags, disclosedAccidentCount])

  // The pill renders nothing when there are no flags, but the component stays
  // mounted, so `opened` would survive. Force it closed whenever there's
  // nothing to show — otherwise flags clearing and later returning would
  // re-render the dropdown already open, with no click from the recruiter.
  useEffect(() => {
    if (groups.length === 0 && opened) setOpened(false)
  }, [groups.length, opened])

  if (!enabled || groups.length === 0) return null

  // Severity breakdown for the header/label: red (MVR over-disclosure) rows read
  // as "severe", orange rows as "caution" — e.g. "2 caution · 1 severe".
  const severeCount = groups.filter((g) => g.dotColor === ERROR_DOT).length
  const cautionCount = groups.length - severeCount
  const severitySummary = [
    cautionCount > 0 && `${cautionCount} caution`,
    severeCount > 0 && `${severeCount} severe`,
  ]
    .filter(Boolean)
    .join(' · ')

  const openDocument = (documentId: string | null) => {
    if (!documentId) return
    navigate(
      routes.applicantDetails({
        id: applicantId,
        tab: 'documents',
        subtab: 'documents',
        documentId,
      })
    )
  }

  const goToCard = (anchorId: string) => {
    document
      .getElementById(anchorId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    <Popover
      position="bottom-start"
      withArrow
      shadow="md"
      width={320}
      opened={opened}
      onChange={setOpened}
    >
      <Popover.Target>
        <button
          type="button"
          aria-label={`AI Flags: ${severitySummary}`}
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
          style={{
            backgroundColor: CHIP_BG,
            color: CHIP_TEXT,
            borderColor: CHIP_BORDER,
          }}
          onClick={() => setOpened((o) => !o)}
        >
          AI
          <span className="inline-flex items-center gap-0.5">
            <IconFlag size={11} />
            {groups.length}
          </span>
        </button>
      </Popover.Target>
      <Popover.Dropdown className="p-0">
        <div className="px-3 py-2 text-[11px] font-medium text-doubleNickel-gray-400">
          {severitySummary}
        </div>
        {groups.map((group) => (
          // The AI vs. Original values are deliberately NOT in the row itself —
          // the open dropdown stays a scannable list of flagged documents, and
          // the field-level detail appears only when the recruiter hovers a row.
          // A HoverCard (not a Tooltip) so the recruiter can move the mouse INTO
          // the panel and scroll long detail lists (e.g. many MVR findings)
          // without it dismissing.
          <HoverCard
            key={group.key}
            withArrow
            shadow="md"
            position="right"
            width={280}
            openDelay={100}
            closeDelay={200}
            // Render the detail panel INSIDE the click-controlled Popover's
            // dropdown (not a body-level portal). Otherwise a mousedown in the
            // panel — e.g. dragging the ScrollArea scrollbar — counts as a click
            // outside the Popover and collapses the whole menu. The Popover
            // dropdown has no overflow clipping, so the panel still shows fully.
            withinPortal={false}
            // Keep the dark Tooltip look (gray-9 bg, white text) now that the
            // detail lives in an interactive, scrollable HoverCard.
            styles={{
              dropdown: {
                backgroundColor: 'var(--mantine-color-gray-9)',
                border: 'none',
                color: 'var(--mantine-color-white)',
              },
              arrow: {
                backgroundColor: 'var(--mantine-color-gray-9)',
                border: 'none',
              },
            }}
          >
            <HoverCard.Target>
              <div className="flex cursor-default select-none items-start gap-2.5 border-t border-doubleNickel-gray-100 px-3 py-2.5">
                <span
                  data-testid={`ai-flag-dot-${group.key}`}
                  className="mt-1 h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: group.dotColor }}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-xs font-semibold text-doubleNickel-gray-900">
                    {group.label}
                  </span>
                  <span className="text-[10px] text-doubleNickel-gray-500">
                    {group.subtitle}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {group.documentId && (
                    <Tooltip label="Open document" withArrow>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        aria-label={`Open ${group.label} document`}
                        onClick={() => openDocument(group.documentId)}
                      >
                        <FileText
                          size={12}
                          className="stroke-doubleNickel-gray-500"
                        />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {group.anchorId ? (
                    <Tooltip label="Go to application section" withArrow>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        aria-label={`Go to ${group.label} on the application`}
                        onClick={() => goToCard(group.anchorId!)}
                      >
                        <ArrowUpRight
                          size={12}
                          className="stroke-doubleNickel-gray-500"
                        />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    // Empty placeholder ActionIcon — same Mantine dimensions as
                    // the real one, so the "Open document" icon stays in the
                    // same column across rows even when this row has no
                    // application section to route to (ENG-4272). Hidden from
                    // assistive tech and tab order; pointer-events disabled so
                    // no spurious hover/focus.
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      aria-hidden="true"
                      tabIndex={-1}
                      data-testid={`ai-flag-arrow-spacer-${group.key}`}
                      style={{ visibility: 'hidden', pointerEvents: 'none' }}
                    />
                  )}
                </div>
              </div>
            </HoverCard.Target>
            <HoverCard.Dropdown className="p-2">
              <ScrollArea.Autosize mah={240} type="auto">
                <div className="flex cursor-default select-none flex-col gap-1.5 pr-3">
                  {group.rows.map((row) => (
                    <div key={row.key} className="flex flex-col">
                      <span className="text-xs font-semibold">{row.label}</span>
                      {row.kind === 'comparison' ? (
                        // Option β: list every source that disagreed, plus the
                        // application's value. Single-source case reads
                        // identically to the pre-engine "AI: x · Original: y"
                        // form, just split across two lines.
                        <>
                          <span className="text-xs">
                            Your app: {row.applicationValue}
                          </span>
                          {row.mismatches.map((m) => (
                            <span
                              key={`${row.key}-${m.sourceLabel}`}
                              className="text-xs"
                            >
                              {m.sourceLabel} says: {m.aiValue}
                            </span>
                          ))}
                        </>
                      ) : (
                        <>
                          <span className="text-xs">{row.detail}</span>
                          {row.description && (
                            // MVR descriptions arrive ALL CAPS off the document;
                            // render lowercase (first-letter cap via CSS) so the
                            // detail reads like prose instead of shouting.
                            <span className="text-xs lowercase text-doubleNickel-gray-300 first-letter:uppercase">
                              {row.description}
                            </span>
                          )}
                          {/* Every other extracted field (ACD code, Points, …) as
                              label/value lines, matching the doc-preview hover. */}
                          {row.details?.map((detail) => (
                            <span
                              key={detail.label}
                              className="text-xs text-doubleNickel-gray-300"
                            >
                              {detail.label}:{' '}
                              {formatExtractedDetailValue(detail.value)}
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea.Autosize>
            </HoverCard.Dropdown>
          </HoverCard>
        ))}
      </Popover.Dropdown>
    </Popover>
  )
}

export default AiFlagsPill
